import React, { useState, useEffect, useCallback } from 'react';
import { Truck, Star, Plus, X, Save, Edit2, Trash2 } from 'lucide-react';
import api from '../api';

const fmt = (v) => v != null && v !== '' ? `$${Number(v).toFixed(2)}` : '$0.00';

/**
 * Multi-vendor card for a product. Lists every vendor linked to this product
 * with their own cost. Admin can:
 *   • Add a vendor (search existing) with a cost price
 *   • Mark one as "Preferred" (inventory consumption + reorders use it first)
 *   • Edit any vendor's cost
 *   • Remove a vendor link
 */
export default function ProductVendorsCard({ productId }) {
  const [rows, setRows] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [addVendorId, setAddVendorId] = useState('');
  const [addCost, setAddCost] = useState('');
  const [addPreferred, setAddPreferred] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editCost, setEditCost] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, v] = await Promise.all([
        api.request(`/api/products/${productId}/vendors`),
        api.getVendors(),
      ]);
      setRows(Array.isArray(r) ? r : []);
      setVendors(Array.isArray(v) ? v : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [productId]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!addVendorId) { alert('Pick a vendor'); return; }
    const v = vendors.find(x => x.id === addVendorId);
    try {
      await api.request(`/api/products/${productId}/vendors`, {
        method: 'POST',
        body: JSON.stringify({
          vendor_id: addVendorId,
          vendor_name: v ? v.name : '',
          cost_price: parseFloat(addCost || '0'),
          preferred: addPreferred,
        }),
      });
      setShowAdd(false);
      setAddVendorId(''); setAddCost(''); setAddPreferred(false);
      load();
    } catch (e) { alert('Failed: ' + e.message); }
  };

  const handlePrefer = async (linkId) => {
    try {
      await api.request(`/api/products/${productId}/vendors/${linkId}/prefer`, { method: 'POST' });
      load();
    } catch (e) { alert('Failed: ' + e.message); }
  };

  const handleDelete = async (linkId, name) => {
    if (!window.confirm(`Remove ${name} from this product's vendor list?`)) return;
    try {
      await api.request(`/api/products/${productId}/vendors/${linkId}`, { method: 'DELETE' });
      load();
    } catch (e) { alert('Failed: ' + e.message); }
  };

  const handleEditCost = async (linkId) => {
    try {
      await api.request(`/api/products/${productId}/vendors/${linkId}`, {
        method: 'PUT',
        body: JSON.stringify({ cost_price: parseFloat(editCost || '0') }),
      });
      setEditingId(null); setEditCost('');
      load();
    } catch (e) { alert('Failed: ' + e.message); }
  };

  // Vendors not yet linked (for the add dropdown)
  const linkedIds = new Set(rows.map(r => r.vendor_id));
  const availableVendors = vendors.filter(v => !linkedIds.has(v.id));

  return (
    <div className="bg-card border rounded-xl p-5" data-testid="product-vendors-card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold flex items-center gap-2"><Truck className="h-5 w-5 text-primary" /> Vendors</h2>
        {!showAdd && (
          <button onClick={() => setShowAdd(true)} className="flex items-center gap-1 text-sm text-primary hover:underline" data-testid="add-vendor-btn">
            <Plus className="h-3.5 w-3.5" /> Add Vendor
          </button>
        )}
      </div>

      {showAdd && (
        <div className="bg-muted/30 border rounded-lg p-3 mb-3 space-y-2" data-testid="add-vendor-form">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Vendor</label>
              <select
                value={addVendorId}
                onChange={e => setAddVendorId(e.target.value)}
                className="w-full px-2 py-1.5 border rounded-md text-sm"
                data-testid="add-vendor-select"
              >
                <option value="">— Pick a vendor —</option>
                {availableVendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Cost from this vendor ($)</label>
              <input
                type="number"
                step="0.01"
                value={addCost}
                onChange={e => setAddCost(e.target.value)}
                className="w-full px-2 py-1.5 border rounded-md text-sm"
                placeholder="0.00"
                data-testid="add-vendor-cost"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input type="checkbox" checked={addPreferred} onChange={e => setAddPreferred(e.target.checked)} data-testid="add-vendor-preferred" />
            Mark as preferred (use this vendor's stock first)
          </label>
          <div className="flex gap-2 justify-end pt-1">
            <button onClick={() => { setShowAdd(false); setAddVendorId(''); setAddCost(''); }} className="px-3 py-1 text-xs border rounded hover:bg-muted">Cancel</button>
            <button onClick={handleAdd} className="px-3 py-1 text-xs bg-primary text-white rounded hover:bg-primary/90" data-testid="add-vendor-save">Add</button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground py-4 text-center">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">No vendors linked yet. Add one above.</p>
      ) : (
        <div className="space-y-2">
          {rows.map(r => (
            <div
              key={r.id}
              className={`flex items-center justify-between px-3 py-2 rounded-md border ${r.preferred ? 'bg-amber-50 border-amber-300' : 'bg-background border-border'}`}
              data-testid={`product-vendor-row-${r.id}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                {r.preferred ? (
                  <Star className="h-4 w-4 fill-amber-500 text-amber-500 flex-shrink-0" />
                ) : (
                  <button
                    onClick={() => handlePrefer(r.id)}
                    className="text-muted-foreground hover:text-amber-500 flex-shrink-0"
                    title="Mark as preferred — used first for reorders & inventory consumption"
                    data-testid={`prefer-vendor-${r.id}`}
                  >
                    <Star className="h-4 w-4" />
                  </button>
                )}
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{r.vendor_name}</div>
                  {r.preferred && <div className="text-xs text-amber-700">Preferred — used first</div>}
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                {editingId === r.id ? (
                  <>
                    <input
                      type="number"
                      step="0.01"
                      value={editCost}
                      onChange={e => setEditCost(e.target.value)}
                      className="w-24 px-2 py-1 border rounded text-sm text-right"
                      autoFocus
                      data-testid={`edit-vendor-cost-input-${r.id}`}
                    />
                    <button onClick={() => handleEditCost(r.id)} className="text-green-600 hover:text-green-700" title="Save" data-testid={`save-vendor-cost-${r.id}`}>
                      <Save className="h-4 w-4" />
                    </button>
                    <button onClick={() => { setEditingId(null); setEditCost(''); }} className="text-muted-foreground hover:text-foreground" title="Cancel">
                      <X className="h-4 w-4" />
                    </button>
                  </>
                ) : (
                  <>
                    <span className="text-sm font-semibold tabular-nums" data-testid={`vendor-cost-${r.id}`}>{fmt(r.cost_price)}</span>
                    <button onClick={() => { setEditingId(r.id); setEditCost(String(r.cost_price ?? '')); }} className="text-muted-foreground hover:text-primary" title="Edit cost" data-testid={`edit-vendor-cost-${r.id}`}>
                      <Edit2 className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => handleDelete(r.id, r.vendor_name)} className="text-muted-foreground hover:text-red-600" title="Remove vendor" data-testid={`delete-vendor-${r.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-muted-foreground mt-3">
        The starred vendor's inventory is consumed first on sales/POS orders, with FIFO as the fallback. Reorders also default to this vendor.
      </p>
    </div>
  );
}
